import { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { updateStore, createStore } from '../../data/commands/storeCommands';
import {
  toFiniteOrNull, fmtCoord, parseLatLngPair, isLat, isLng,
} from '../../utils/coords';
import Icon from '../../components/Icon';

// Edit/Add hub modal. Edit moves an existing hub (lat/lng/radius); Add creates one.
function StoreModal({ store, tenantId, actor, onClose }) {
  const editing = Boolean(store);
  const [form, setForm] = useState({
    name: store?.name ?? '', address: store?.address ?? '',
    lat: store?.lat ?? '', lng: store?.lng ?? '', radiusKm: store?.radiusKm ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  // Convenience: pasting "lat, lng" (as copied from Google Maps) into Latitude
  // fills both fields instead of producing a broken value.
  const setLat = (e) => {
    const pair = parseLatLngPair(e.target.value);
    if (pair) { setForm((f) => ({ ...f, lat: String(pair.lat), lng: String(pair.lng) })); return; }
    setForm((f) => ({ ...f, lat: e.target.value }));
  };

  async function save() {
    if (!form.name.trim()) { setError('Store name is required.'); return; }
    const lat = toFiniteOrNull(form.lat);
    const lng = toFiniteOrNull(form.lng);
    const radiusKm = toFiniteOrNull(form.radiusKm);
    // Validate coordinates so a bad value never reaches Firestore or the map.
    if (form.lat !== '' && !isLat(lat)) { setError('Latitude must be a number between -90 and 90.'); return; }
    if (form.lng !== '' && !isLng(lng)) { setError('Longitude must be a number between -180 and 180.'); return; }
    if (form.radiusKm !== '' && (radiusKm == null || radiusKm <= 0)) { setError('Coverage radius must be a positive number.'); return; }
    setSaving(true); setError('');
    try {
      if (editing) {
        await updateStore(store.id, {
          name: form.name, address: form.address, lat, lng, radiusKm,
        }, actor);
      } else {
        await createStore({
          tenantId, name: form.name, address: form.address, lat, lng, radiusKm,
        }, actor);
      }
      onClose();
    } catch (e) {
      setError(e.message || 'Could not save the store.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-scrim" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header><Icon name="store" size={18} /> {editing ? 'Edit store' : 'Add store'}</header>
        <div className="body">
          <div className="field"><label>Store name</label><input className="input" value={form.name} onChange={set('name')} placeholder="Presto Hub 2 — BGC" /></div>
          <div className="field"><label>Address</label><input className="input" value={form.address} onChange={set('address')} /></div>
          <div className="grid2">
            <div className="field"><label>Latitude</label><input className="input" value={form.lat} onChange={setLat} placeholder="14.857142 (or paste “lat, lng”)" /></div>
            <div className="field"><label>Longitude</label><input className="input" value={form.lng} onChange={set('lng')} placeholder="120.864714" /></div>
          </div>
          <div className="field"><label>Coverage radius (km)</label><input className="input" value={form.radiusKm} onChange={set('radiusKm')} /></div>
          {error && <div className="form-error">{error}</div>}
        </div>
        <footer>
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : (editing ? 'Save hub' : 'Create store')}</button>
        </footer>
      </div>
    </div>
  );
}

export default function Stores() {
  const { tenantId, authUser, profile, role } = useAuth();
  const [stores, setStores] = useState([]);
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null); // {store} for edit, {} for add, null closed
  const actor = { uid: authUser?.uid, name: profile?.name, role };

  useEffect(() => {
    if (!tenantId) { setStores([]); return undefined; }
    const q = query(collection(db, 'stores'), where('tenantId', '==', tenantId));
    return onSnapshot(q, (snap) => setStores(snap.docs.map((d) => ({ id: d.id, ...d.data() }))), (e) => setError(e.message || 'Could not load stores.'));
  }, [tenantId]);

  return (
    <div>
      <div className="toolbar">
        <span style={{ flex: 1 }} />
        <button className="btn btn-primary" onClick={() => setModal({})}><Icon name="plus" size={15} /> Add store</button>
      </div>
      {error && <div className="form-error">{error}</div>}
      <div className="table-wrap">
        <table>
          <thead><tr><th>Store</th><th>Address</th><th>Lat</th><th>Lng</th><th>Coverage</th><th>Status</th><th /></tr></thead>
          <tbody>
            {stores.map((s) => (
              <tr key={s.id}>
                <td style={{ fontWeight: 'var(--font-semibold)' }}>{s.name}</td>
                <td className="muted">{s.address ?? '—'}</td>
                <td>{fmtCoord(s.lat)}</td>
                <td>{fmtCoord(s.lng)}</td>
                <td>{s.radiusKm != null ? `${Number(s.radiusKm).toFixed(1)} km` : '—'}</td>
                <td><span className="badge" style={{ background: 'var(--presto-success-bg)', color: 'var(--presto-success)' }}>{s.status ?? 'active'}</span></td>
                <td style={{ textAlign: 'right' }}><button className="btn btn-ghost btn-sm" onClick={() => setModal({ store: s })}>Edit</button></td>
              </tr>
            ))}
            {stores.length === 0 && !error && <tr><td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 'var(--space-8)' }}>No stores in this tenant yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ marginTop: 12, fontSize: 'var(--text-caption)' }}>A tenant can have many stores; each store owns its own territories. Editing lat/lng is how you move a hub — no code change.</p>
      {modal && <StoreModal store={modal.store} tenantId={tenantId} actor={actor} onClose={() => setModal(null)} />}
    </div>
  );
}
